// taxonomy.js — THE identity tree and the three capability systems, as DATA.
//
// Owner ruling 2026-08-01 (SEARCH_GRAMMAR.md §5b). Two ideas live here and nowhere else:
//
//  1. TAXONOMY — Object → Intent → Category → Kind → Subtype. The TREE renders this; the
//     interview panel never asks it again. A level appears only when the previous one leaves
//     MORE THAN ONE valid child (Commercial has a single Kind, so its subtypes surface
//     directly). Every node resolves to a PREDICATE SET over the shared search state — the
//     same `dset` pattern districts already use — never to a storage hierarchy.
//
//  2. CAPABILITY — three ORTHOGONAL profiles that INTERSECT to produce the interview:
//        Identity (kind/subtype) → physical applicability
//        Intent                  → legal / financial / move-in / deposit
//        Object                  → workflow: claims, exposure, agent, lead state
//     The intersection is why this is rows-and-sets rather than dozens of pairwise `&&`
//     conditions in JSX. It generalises `kind_attribute_capability` (0345) from one dimension
//     to three; when the DB grows the other two, this module becomes its projection.
//
// ⚠️ COVERAGE IS NOT TAXONOMY (owner). A node with one property keeps its place beside a node
// with 791. Thin data earns a BADGE, never a removed or reordered branch — the tree describes
// reality, coverage describes confidence. `coverageBadge()` is the only thing that may read
// counts, and it may not change the shape.

// ── The identity tree ────────────────────────────────────────────────────────
// Category is NEW (2026-08-01): it had no representation in code at all — PROPERTY_KIND_OPTIONS
// was a flat four. It is a real grammar level, not a UI grouping.
export const CATEGORIES = ['residential', 'commercial', 'land']

export const CATEGORY_KINDS = {
  residential: ['apartment', 'room', 'house'],
  commercial: ['commercial'],
  land: ['land'],
}

// Subtype per kind. Tokens are canonical (`subtype_map.js` / the server whitelist / the box
// vocabulary) — a new one is a GRAMMAR change first. Order = corpus size, so the useful
// branches come first; that is presentation, and it is allowed to read counts. The SET is not.
export const KIND_SUBTYPES = {
  land: ['agricultural', 'non_agricultural'],
  commercial: ['hotel', 'production_space', 'office', 'universal_space', 'warehouse',
    'restaurant_cafe', 'retail', 'garage', 'parking_space', 'non_residential_space',
    'special_purpose'],
  house: ['cottage', 'country_house', 'townhouse', 'villa'],
  // apartment: NO subtype slot, by ruling. Its third level is the STATUS axis below.
}

// The (b)-axis, Phase 0.5 ruling #2: building status is a NAVIGATION convenience and an
// ORTHOGONAL predicate — never a subtype, never a storage hierarchy. Internally the row stays
// `kind=apartment` + a feature code. It renders as apartment's third tree level because that
// is where an agent looks for it; 88.9% of apartments carry one (measured 2026-08-01:
// cls_new_built 14,967 · cls_old_built 7,830 · cls_under_constr 647).
// ⚠️ The remaining 11% MUST be reachable as an explicit `unknown` child, never hidden —
// unknown ≠ no is law (principle 4).
export const APARTMENT_STATUS = ['cls_new_built', 'cls_old_built', 'cls_under_constr']

// ── Residential nodes that are an APARTMENT plus a context (owner 2026-10-02) ──
// An apart-hotel unit is physically an apartment; "apart-hotel" says how its building is RUN
// (reception, cleaning, an operator's rental pool). So it is NOT a kind and NOT a subtype —
// the row stays `kind=apartment` and carries the feature `type_aparthotel_unit` (core 1940,
// owner 2026-09-22: "an APARTMENT sub-type, not commercial"). Only the TREE draws it as a
// sibling of apartment, because that is where an agent looks for it.
// ⚠️ Not the same thing as commercial `hotel` (a whole hotel business / building) — never merge.
// Same shape as APARTMENT_STATUS: a navigation node that writes a feature predicate.
// node token → the feature code it writes. The kind it sits on is always `apartment`.
export const APARTMENT_CONTEXT = { aparthotel: 'type_aparthotel_unit' }
export const APARTMENT_CONTEXT_CODES = Object.values(APARTMENT_CONTEXT)
// Where each context row sits in its category's kind list (right after apartment).
export const CATEGORY_CONTEXT_NODES = { residential: ['aparthotel'] }

// ── One glyph per node (2026-08-10) ──────────────────────────────────────────
// Material Symbols names, resolved through Icon.jsx's MATERIAL registry. Keyed on the SAME
// tokens the tree above walks, so a node cannot exist without the map being asked for it and
// a rename breaks in one place instead of drifting silently.
//
// They live HERE and not in the rail because the tree is not the only surface that draws these
// nodes — the Type facet chips and the constraint chips render the same tokens, and a second
// hand-written map is how one ends up saying `garage` where the other says `house`. Same
// reason CATEGORY_KINDS is not inlined in the rail, and the same reason roles go through
// Icon.jsx's ROLE_ICON.
//
// ⚠️ This is PRESENTATION, exactly like the ordering note above: it may not add, remove or
// reorder a node. A token with no entry renders with no icon, which is correct — a missing
// glyph is honest, an approximate one is a claim about what the node means.
// ⚠️ INTENT is a SEPARATE namespace from KIND. Both would collide on bare names and the rail
// draws one row from each in the same column. Do not merge them.
export const INTENT_ICON = {
  sale: 'sell',
  // ⚠️ NOT a key — see the note beside `payments` in Icon.jsx. `key_vertical` is OWNER and
  // `key` is withheld knowledge; a third key on the rail's busiest row helps nobody.
  rent: 'payments',
  daily_rent: 'bedtime',
  girao: 'account_balance',
}
export const CATEGORY_ICON = {
  residential: 'home',
  commercial: 'store',
  land: 'landscape',
}
export const KIND_ICON = {
  apartment: 'apartment',
  // A context node, not a kind — it IS an apartment, so it draws the apartment glyph rather
  // than commercial hotel's, which would claim the two are one thing.
  aparthotel: 'apartment',
  room: 'meeting_room',
  house: 'house',
  commercial: 'store',
  land: 'landscape',
}
export const SUBTYPE_ICON = {
  agricultural: 'agriculture',
  non_agricultural: 'crop_landscape',
  hotel: 'hotel',
  production_space: 'factory',
  office: 'business_center',
  universal_space: 'dashboard',
  warehouse: 'warehouse',
  restaurant_cafe: 'restaurant',
  retail: 'storefront',
  garage: 'garage',
  parking_space: 'local_parking',
  non_residential_space: 'meeting_room',
  special_purpose: 'settings_suggest',
  cottage: 'cottage',
  country_house: 'holiday_village',
  townhouse: 'roofing',
  villa: 'villa',
}

// ── The tree walk ────────────────────────────────────────────────────────────
// One function answers "what is the next question?", so the tree component renders a list and
// owns no taxonomy logic of its own.
//
// `path` is what the agent has already chosen: { intent, category, kind, subtype }.
// Returns { level, options } — or null when identity is fully established and the interview
// takes over.
export function nextIdentityLevel(path = {}) {
  const { intent, category, kind } = path
  if (!intent) return { level: 'intent', options: ['sale', 'rent', 'daily_rent', 'girao'] }
  if (!category) return { level: 'category', options: CATEGORIES }

  const kinds = CATEGORY_KINDS[category] || []
  // THE PROGRESSIVE RULE: a level with one child is not a question. Commercial and Land each
  // have a single kind, so the tree skips straight past Kind to their subtypes.
  if (!kind) {
    if (kinds.length > 1) return { level: 'kind', options: kinds }
    if (kinds.length === 1) return nextIdentityLevel({ ...path, kind: kinds[0] })
    return null
  }

  if (kind === 'apartment') {
    return { level: 'status', options: APARTMENT_STATUS, orthogonal: true, unknownChild: true }
  }
  const subs = KIND_SUBTYPES[kind] || []
  if (subs.length > 1 && !path.subtype) return { level: 'subtype', options: subs }
  return null
}

// The kind a category resolves to when it has exactly one — so a `commercial` tree node can
// still write `kinds:['commercial']` even though the Kind question was never asked.
export function impliedKind(category) {
  const kinds = CATEGORY_KINDS[category] || []
  return kinds.length === 1 ? kinds[0] : null
}

export const KIND_CATEGORY = Object.fromEntries(
  Object.entries(CATEGORY_KINDS).flatMap(([cat, kinds]) => kinds.map(k => [k, cat]))
)

// A tree node → the predicate set it writes into the SHARED search state. The tree owns no
// state of its own; it edits the same `advFilters` the panel, the box and the URL all edit.
export function identityPatch({ category, kind, subtype, status, context } = {}) {
  const patch = {}
  // A context node (apart-hotel) is an apartment: it forces the kind, whatever was passed.
  const ctxCode = context ? APARTMENT_CONTEXT[context] : null
  const k = ctxCode ? 'apartment' : (kind || impliedKind(category))
  if (k) patch.kinds = [k]
  else if (category) patch.kinds = [...(CATEGORY_KINDS[category] || [])]
  if (subtype) patch.subtypes = [subtype]
  // Status is a FEATURE predicate, not identity — it rides featureCodes exactly as the
  // Characteristics panel would write it. This is the ruling's "orthogonal predicate,
  // rendered indented" made concrete. The apart-hotel context rides the same list.
  const codes = [ctxCode, status].filter(Boolean)
  if (codes.length) patch.featureCodes = codes
  return patch
}

// ── The INVERSE: state → tree selection ──────────────────────────────────────
// 🔒 THE TAXONOMY PROJECTS ONTO STATE; IT NEVER BECOMES THE STATE (owner, SEARCH_GRAMMAR §5b).
//
// Canonical state is explicit predicate fields (kinds / subtypes / featureCodes / intents).
// The tree DERIVES which node is lit from those — this function — and never stores a node
// path like `selectedNode = "Apartment → New"`. That direction is what keeps URLs stable, the
// API explicit, saved searches independent of tree structure, and the tree redesignable
// without migrating anyone's persisted search.
//
// ⚠️ `category` is DERIVED, not stored — deliberately, for the owner's own reason: a stored
// category would make old saved searches depend on today's grouping, so regrouping categories
// later would silently change what they mean. Kinds are the durable fact.
export function deriveIdentityPath(filters = {}) {
  const intents = filters.intents || []
  const kinds = filters.kinds || []
  const subtypes = filters.subtypes || []
  const codes = filters.featureCodes || []

  // A tree node is a SINGLE choice. Two kinds checked in the panel is a legitimate search but
  // not a tree position, so it lights no kind node — the tree reports what it can and stays
  // silent rather than guessing.
  const kind = kinds.length === 1 ? kinds[0] : null
  let category = kind ? KIND_CATEGORY[kind] : null
  if (!category && kinds.length > 1) {
    // Exactly the members of one category = that category's node (Residential = apartment+house).
    const want = [...kinds].sort().join(',')
    category = CATEGORIES.find(c => [...(CATEGORY_KINDS[c] || [])].sort().join(',') === want) || null
  }
  // The apart-hotel node is lit only on an apartment search carrying its code — the same code
  // on a multi-kind search is just a characteristic, not a tree position.
  const context = kind === 'apartment'
    ? (Object.keys(APARTMENT_CONTEXT).find(n => codes.includes(APARTMENT_CONTEXT[n])) || null)
    : null
  return {
    intent: intents.length === 1 ? intents[0] : null,
    category,
    kind,
    subtype: subtypes.length === 1 ? subtypes[0] : null,
    status: codes.find(c => APARTMENT_STATUS.includes(c)) || null,
    context,
  }
}

// ── Capability system 1 of 3: IDENTITY → physical applicability ──────────────
// 'R' required · 'O' optional · 'M' meaningless (unavailable) · 'U' unknown until subtype.
// Mirrors SEARCH_GRAMMAR.md §8 and the DB's kind_attribute_capability (0345).
// `condition` = the მდგომარეობა facet (რემონტი + კარკასი ladders, 0433/v37).
// Owner ruling 2026-08-06 (B342): **land only.** A plot has no state of repair, so the
// question is meaningless there. COMMERCIAL KEEPS IT, against residential intuition —
// measured: 86 of the 115 rent+frame properties are commercial, because letting a bare
// commercial shell is ordinary practice here. Hiding it for commercial would have hidden
// real stock. Do not "tidy" this into a residential-only rule without re-measuring.
const IDENTITY_BASE = {
  apartment: { rooms: 'R', bedrooms: 'R', bathrooms: 'O', area: 'R', landArea: 'M', floor: 'R', floorsTotal: 'O', topFloor: 'O', elevator: 'O', condition: 'O' },
  house: { rooms: 'R', bedrooms: 'R', bathrooms: 'O', area: 'R', landArea: 'R', floor: 'M', floorsTotal: 'O', topFloor: 'M', elevator: 'M', condition: 'O' },
  // 1217: a room in a shared flat. It IS one room, so room/bedroom counts are meaningless; the rest
  // is the flat it sits in (floor, lift, state of repair).
  room: { rooms: 'M', bedrooms: 'M', bathrooms: 'O', area: 'O', landArea: 'M', floor: 'O', floorsTotal: 'O', topFloor: 'O', elevator: 'O', condition: 'O' },
  land: { rooms: 'M', bedrooms: 'M', bathrooms: 'M', area: 'M', landArea: 'R', floor: 'M', floorsTotal: 'M', topFloor: 'M', elevator: 'M', condition: 'M' },
  commercial: { rooms: 'U', bedrooms: 'U', bathrooms: 'O', area: 'R', landArea: 'O', floor: 'O', floorsTotal: 'O', topFloor: 'O', elevator: 'O', condition: 'O' },
}
// Subtype SPECIALISES its kind — inheritance, exactly as §5 rules. Only the U cells that
// subtype can actually resolve are listed; everything else falls through to the kind.
const IDENTITY_SUBTYPE = {
  hotel: { rooms: 'R', bedrooms: 'R' },
  office: { rooms: 'O', bedrooms: 'M' },
  warehouse: { rooms: 'M', bedrooms: 'M' },
  agricultural: { landArea: 'R' },
  non_agricultural: { landArea: 'R' },
}

export function identityCapability({ kind, subtype } = {}) {
  const base = IDENTITY_BASE[kind] || {}
  return { ...base, ...(subtype ? IDENTITY_SUBTYPE[subtype] || {} : {}) }
}

// ── Capability system 2 of 3: INTENT → legal / financial / terms ─────────────
// ⚠️ Note what is ABSENT: any instruction to hide `restrictions`. B269 measured
// listing_policy rows on sale (4) and girao (8) — buyer restrictions are real (0080), so the
// grammar's original "Sale − Restrictions" line was wrong and is deliberately not encoded.
// `advance` is the one term measurement confirms is rent-only.
const INTENT_PROFILE = {
  sale: { advance: 'M', deposit: 'M', mortgageable: 'O', ownership: 'O', vat: 'O', legalEntity: 'O', moveIn: 'O', restrictions: 'O', furnishing: 'O' },
  rent: { advance: 'O', deposit: 'O', mortgageable: 'M', ownership: 'M', vat: 'M', legalEntity: 'M', moveIn: 'O', restrictions: 'O', furnishing: 'O' },
  daily_rent: { advance: 'M', deposit: 'O', mortgageable: 'M', ownership: 'M', vat: 'M', legalEntity: 'M', moveIn: 'M', restrictions: 'O', furnishing: 'O' },
  girao: { advance: 'M', deposit: 'M', mortgageable: 'M', ownership: 'O', vat: 'M', legalEntity: 'O', moveIn: 'O', restrictions: 'O', furnishing: 'O' },
}
// No intent chosen = no intent constraint. Absence is not a profile.
export function intentCapability(intents = []) {
  const list = (intents || []).filter(i => INTENT_PROFILE[i])
  if (!list.length) return {}
  // Multi-select is a UNION: asking for sale OR rent must not remove what either allows.
  const out = {}
  for (const i of list) {
    for (const [k, v] of Object.entries(INTENT_PROFILE[i])) {
      out[k] = out[k] === undefined ? v : (out[k] === 'M' && v === 'M' ? 'M' : 'O')
    }
  }
  return out
}

// ── Capability system 3 of 3: OBJECT → workflow ──────────────────────────────
// Which workflow filters mean anything for the object being searched. `Client` is a
// demand, not a listing, so exposure/claims do not apply.
// Leads.terms went 'M' → 'O' on 2026-08-05: the 'M' dated from when the Terms trio was
// client-only over loaded rows the leads feed never loads. All three are SERVER predicates
// now (restrictions 0244/0245, advance+move-in with B177's close — LOCAL_ONLY_ADV_KEYS is
// empty), every scope's filtered path lands on the ONE search RPC (v34), and rules reach it
// through cluster_restriction_evidence_v1 (0409). A restriction lens over the leads
// inventory is exactly the "find me listings that take this client" question agents ask.
const OBJECT_PROFILE = {
  Property: { claims: 'O', exposure: 'O', agent: 'O', leadState: 'M', terms: 'O', intelligence: 'O', listingOnly: 'O' },
  Listings: { claims: 'O', exposure: 'O', agent: 'O', leadState: 'M', terms: 'O', intelligence: 'O', listingOnly: 'O' },
  Leads: { claims: 'O', exposure: 'O', agent: 'O', leadState: 'O', terms: 'O', intelligence: 'O', listingOnly: 'O' },
  Client: { claims: 'M', exposure: 'M', agent: 'O', leadState: 'M', terms: 'M', intelligence: 'M', listingOnly: 'M' },
  Meeting: { claims: 'M', exposure: 'M', agent: 'O', leadState: 'M', terms: 'M', intelligence: 'M', listingOnly: 'M' },
  Business: { claims: 'M', exposure: 'M', agent: 'M', leadState: 'M', terms: 'M', intelligence: 'M', listingOnly: 'M' },
}
export function objectCapability(objectType) {
  return OBJECT_PROFILE[objectType] || OBJECT_PROFILE.Property
}

// ── The intersection — this is the interview ─────────────────────────────────
// 'M' from ANY of the three wins: if any system says the question is meaningless, it is.
// Everything else resolves to the strongest surviving statement (R > O).
export function interviewCapability({ kind, subtype, intents, objectType } = {}) {
  const parts = [identityCapability({ kind, subtype }), intentCapability(intents), objectCapability(objectType)]
  const out = {}
  for (const p of parts) {
    for (const [k, v] of Object.entries(p)) {
      if (out[k] === 'M' || v === 'M') out[k] = 'M'
      else if (out[k] === 'R' || v === 'R') out[k] = 'R'
      else out[k] = out[k] ?? v
    }
  }
  return out
}

// The only question a component should have to ask.
export const applies = (cap, key) => (cap[key] ?? 'O') !== 'M'

// ── Higher hierarchy wins: an inapplicable filter is IGNORED, not applied ─────
// Owner ruling 2026-08-01: *"land has no rooms — the filter must respect the higher
// hierarchy chip and ignore the lower when it doesn't exist."*
//
// THE BUG THIS FIXES: `kind_attribute_capability` says land/rooms is not applicable, so the
// RPC's range check returns 'na', and `classified` maps 'na' → MISMATCH — every land property
// excluded. Meanwhile the panel had correctly HIDDEN the Rooms control. So a filter the UI
// said did not apply was silently emptying the result set, with its cause invisible.
//
// The ruling makes identity authoritative: Land is the higher statement, Rooms the lower, and
// where the lower cannot exist it is simply not asked. So these keys are stripped from the
// query and from the chip row — **but NOT from state**: the value survives so that returning
// to a compatible identity restores it. Ignoring is not deleting; a navigation click must
// never destroy what the agent typed.
//
// One capability key can own several filter fields (a range is two).
export const CAPABILITY_FILTER_KEYS = {
  rooms: ['roomsSet'],
  bedrooms: ['bedroomsSet'],
  bathrooms: ['bathroomsMin', 'bathroomsMax'],
  floor: ['floorMin', 'floorMax'],
  floorsTotal: ['floorsTotalMin', 'floorsTotalMax'],
  // ⚠️ Desk filters audit 2026-10-28 DF06: a capability owns BOTH polarities and the first-floor pair
  // (the panel says first floor shares top floor's capability). Listing only the positive halves sent
  // first_floor / top_floor_not / condition_not for LAND while the panel called them inapplicable.
  topFloor: ['topFloor', 'topFloorNot', 'firstFloor', 'firstFloorNot'],
  area: ['areaMin', 'areaMax'],
  landArea: ['landAreaMin', 'landAreaMax'],
  // Listing it here is what makes a stranded condition choice SAFE: switching to land strips
  // conditionCodes from the query and the chip row while leaving it in state, so switching
  // back restores it. That is the field-level half of the D ruling; the panel-level half
  // (show it disabled and removable rather than silently dropping it) is B350.
  condition: ['conditionCodes', 'conditionExcludeCodes'],
  advance: ['advanceMaxMonths'],
  terms: ['restrictions', 'advanceMaxMonths', 'moveInBy', 'moveInHard'],
  // ⚠️ DF05 (2026-10-28): every filter only a LISTING can answer. deskFeed drops client and meeting
  // rows whenever one is active (listingOnlyLens), so a per-m² price or a claims bound carried from
  // Property into Client emptied the Client search with its control already hidden. Projecting them
  // out for those objects keeps them in state (Property restores them) and shows them as stranded.
  // The keys themselves live in deskFeed (SERVER_ONLY_ADV_KEYS); the caller passes them in so this
  // file stays free of the feed (it is bundled into the server's public query).
  listingOnly: [],
}

// Which filter FIELDS the current identity/intent/object makes meaningless.
export function inapplicableFilterKeys(filters = {}, objectType = 'Property', listingOnlyKeys = []) {
  const path = deriveIdentityPath(filters)
  const cap = interviewCapability({
    kind: path.kind, subtype: path.subtype,
    intents: filters.intents || [], objectType,
  })
  const out = new Set()
  for (const [capKey, fields] of Object.entries(CAPABILITY_FILTER_KEYS)) {
    if (!applies(cap, capKey)) for (const f of (capKey === 'listingOnly' ? listingOnlyKeys : fields)) out.add(f)
  }
  return out
}

// The filters as the QUERY should see them. Pure; never mutates. `defaults` supplies the
// neutral value per field so the shape the fetch layer expects is preserved ('' vs [] vs
// false) — a raw `delete` would make `f.roomsSet.length` throw downstream.
export function applicableFilters(filters = {}, objectType = 'Property', defaults = null, listingOnlyKeys = []) {
  const dead = inapplicableFilterKeys(filters, objectType, listingOnlyKeys)
  if (!dead.size) return filters
  const out = { ...filters }
  for (const k of dead) {
    if (!(k in out)) continue
    out[k] = defaults && k in defaults ? defaults[k]
      : Array.isArray(out[k]) ? [] : (typeof out[k] === 'boolean' ? false : '')
  }
  return out
}

// ── Coverage: confidence, never shape ────────────────────────────────────────
// Returns a BADGE for a node, or null. It may not reorder or remove anything — the ruling is
// explicit that the tree describes reality and coverage describes confidence.
export function coverageBadge(n) {
  if (n == null) return null          // not measured yet ≠ empty
  if (n === 0) return 'none'          // declared slot, no supply yet (villa, townhouse…)
  if (n < 25) return 'limited'
  return null
}
